import React, { useState, useEffect, useRef } from "react";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import { getTimesheets, createTimesheet, updateTimesheet, getAllProjects, getProjectTasks, isAuthError, forceLogout } from "../service/rpc";
import { useUser } from "../context/UserContext";
import toast from "react-hot-toast";
import Modal from "../components/Modal";


const ITEMS_PER_PAGE = 6;
const EMPTY_FORM = { project: "", project_id: "", task: "", task_id: "", task_name: "", date: "", hours: "" };

export default function TimesheetsPage() {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewType, setViewType] = useState("list");
  const [projects, setProjects] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const { uid, loading: userLoading } = useUser();

  // Inline editing states
  const [isCreatingTs, setIsCreatingTs] = useState(false);
  const [editingTsId, setEditingTsId] = useState(null);
  const [tsForm, setTsForm] = useState(EMPTY_FORM);
  const [tsSaving, setTsSaving] = useState(false);
  const [projectTasks, setProjectTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [isTsModalOpen, setIsTsModalOpen] = useState(false);

  const lastSavedForm = useRef(null);

  //logic of kanban view in mobile 

  useEffect(() => {
    if (window.innerWidth <= 992) {
      setViewType("grid");
    }
  }, [])

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;
        let domain = [];

        if (search.trim()) {
          const q = search.trim();
          const searchDomain = ['|', ['name', 'ilike', q], ['project_id', 'ilike', q]];
          domain = domain.length > 0 ? ['&', ...domain, ...searchDomain] : searchDomain;
        }

        const [{ timesheets: tsData, count }, { projects: projectsData }] = await Promise.all([
          getTimesheets(domain, ITEMS_PER_PAGE, offset),
          getAllProjects([], 200, 0)
        ]);

        const mappedData = tsData.map(ts => ({
          id: ts.id,
          project: ts.project_id ? ts.project_id[1] : "-",
          project_id_raw: ts.project_id,
          date: (ts.date_start && typeof ts.date_start === 'string') ? ts.date_start.split(' ')[0] : (ts.create_date ? ts.create_date.split(' ')[0] : "N/A"),
          hours: (ts.duration || 0).toFixed(2),
          task_name: ts.task_id ? ts.task_id[1] : "-",
          task_id_raw: ts.task_id,
          description: ts.name || "-",
          status: "Approved"
        }));
        setTimesheets(mappedData);
        setTotalItems(count);
        setProjects(projectsData);

      } catch (err) {
        console.error("Failed to fetch data:", err);
        if (isAuthError(err)) { forceLogout(); return; }
        setError(err.isServerDown
          ? "Server is temporarily unavailable. Please try again later."
          : "Error loading timesheets. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    }
    if (!userLoading && uid) {
      fetchData();
    }
  }, [search, currentPage, uid, userLoading]);

  // Fetch tasks when project changes
  useEffect(() => {
    async function fetchTasks() {
      if (!tsForm.project_id) {
        setProjectTasks([]);
        return;
      }
      try {
        setTasksLoading(true);
        const domain = [['project_id', '=', parseInt(tsForm.project_id)]];
        const tasks = await getProjectTasks(domain);
        setProjectTasks(tasks.map(t => ({ id: t.id, name: t.name })));
      } catch (err) {
        console.error("Failed to fetch tasks", err);
      } finally {
        setTasksLoading(false);
      }
    }
    fetchTasks();
  }, [tsForm.project_id]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    const initial = { ...EMPTY_FORM, date: today };
    setTsForm(initial);
    lastSavedForm.current = initial;
    setIsTsModalOpen(true);
    setEditingTsId(null);
  };

  const cancelTsEdit = () => {
    setIsTsModalOpen(false);
    setEditingTsId(null);
    setTsForm(EMPTY_FORM);
  };

  const handleTsSave = async () => {
    const isDirty = JSON.stringify(tsForm) !== JSON.stringify(lastSavedForm.current);
    if (!isDirty && !isCreatingTs) return;

    try {
      setTsSaving(true);
      const payload = {
        name: tsForm.task || tsForm.task_name || "-", // Prioritize manual description
        project_id: tsForm.project_id ? parseInt(tsForm.project_id) : false,
        task_id: tsForm.task_id ? parseInt(tsForm.task_id) : false,
        duration: parseFloat(tsForm.hours) || 0,
        user_id: uid,
        date_start: tsForm.date,
      };

      if (!uid) {
        toast.error("You must be logged in to save timesheets.");
        setTsSaving(false);
        return;
      }

      if (isCreatingTs) {
        const newId = await createTimesheet(payload);
        const selProj = projects.find(p => p.id === parseInt(tsForm.project_id));
        setTimesheets(prev => [{
          id: newId,
          project: selProj ? selProj.name : "-",
          project_id_raw: selProj ? [selProj.id, selProj.name] : null,
          date: tsForm.date,
          hours: parseFloat(tsForm.hours) || 0,
          task_name: tsForm.task_name || "-",
          description: tsForm.task || "-",
          status: "Approved"
        }, ...prev]);
        setTotalItems(prev => prev + 1);
      } else {
        await updateTimesheet(editingTsId, payload);
        const selProj = projects.find(p => p.id === parseInt(tsForm.project_id));
        setTimesheets(prev => prev.map(ts => 
          ts.id === editingTsId 
          ? { 
              ...ts, 
              ...payload, 
              hours: parseFloat(tsForm.hours),
              project: selProj ? selProj.name : ts.project,
              project_id_raw: [parseInt(tsForm.project_id)],
              task_id_raw: [parseInt(tsForm.task_id)],
              description: tsForm.task
            }
          : ts
        ));
      }
      
      lastSavedForm.current = { ...tsForm }; // Mark as clean after save
      toast.success(isCreatingTs ? "Logged!" : "Saved!");
      cancelTsEdit();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Save failed.");
    } finally {
      setTsSaving(false);
    }
  };

const getRowBgColor = (ts, allTs) => {
  if (!ts || !allTs) return 'transparent';

  const project_id = ts.project_id_raw
    ? ts.project_id_raw[0]
    : null;

  if (!project_id) return 'transparent';

  const sameProjectDayEntries = allTs.filter(item => {
    const itemPid = item.project_id_raw
      ? item.project_id_raw[0]
      : null;

    return (
      itemPid === project_id &&
      item.date === ts.date
    );
  });

  const totalHours = sameProjectDayEntries.reduce(
    (sum, item) => sum + parseFloat(item.hours || 0),
    0
  );

  // Progress (1–7h) → Sky Blue
  if (totalHours >= 1 && totalHours < 8) {
    return '#EFF8FF';
  }

  // Completed (exactly 8h) → Green
  if (totalHours === 8) {
    return '#F0FDF4';
  }

  // Overtime (>8h) → Red
  if (totalHours > 8) {
    return '#FEF2F2';
  }

  // No hours logged → White
  return '#ffffff';
};
  const pagedData = timesheets;


  if (error) return (
    <main className="main">
      <div className="error-container" style={{ textAlign: 'center', padding: '100px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>
          {error.includes('unavailable') ? '🔌' : '⚠️'}
        </div>
        <h2 style={{ color: '#1e293b', marginBottom: '10px', fontFamily: 'Sora' }}>
          {error.includes('unavailable') ? 'Server Unavailable' : 'Something went wrong'}
        </h2>
        <p style={{ color: '#64748b', marginBottom: '20px' }}>{error}</p>
        <button className="btn-create" onClick={() => window.location.reload()}>Retry</button>
      </div>
    </main>
  );

  return (
    <main className="main">
      <Modal
        isOpen={isTsModalOpen}
        onClose={cancelTsEdit}
        title={editingTsId ? "Edit Timesheet" : "Log New Time"}
        onSave={handleTsSave}
        saving={tsSaving}
      >
        <div className="modal-form">
          <div className="form-group">
            <label className="form-label">Project</label>
            <select 
              className="form-input" 
              value={tsForm.project_id} 
              onChange={e => setTsForm({ ...tsForm, project_id: e.target.value })}
            >
              <option value="">Select Project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Task</label>
            <select 
              className="form-input" 
              value={tsForm.task_id} 
              onChange={e => {
                const sel = projectTasks.find(t => t.id === parseInt(e.target.value));
                setTsForm({ ...tsForm, task_id: e.target.value, task_name: sel ? sel.name : "" });
              }}
            >
              <option value="">{tasksLoading ? "Loading..." : "Select Task"}</option>
              {projectTasks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input 
                className="form-input" 
                type="date" 
                value={tsForm.date} 
                onChange={e => setTsForm({ ...tsForm, date: e.target.value })} 
              />
            </div>
            <div className="form-group">
              <label className="form-label">Hours</label>
              <input 
                className="form-input" 
                type="number" 
                step="0.5" 
                value={tsForm.hours} 
                onChange={e => setTsForm({ ...tsForm, hours: e.target.value })} 
                placeholder="0.0" 
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description / Work Details</label>
            <textarea 
              className="form-input" 
              style={{ minHeight: '100px' }} 
              value={tsForm.task} 
              onChange={e => setTsForm({ ...tsForm, task: e.target.value })} 
              placeholder="What did you work on?" 
              rows={3} 
            />
          </div>
        </div>
      </Modal>
      <div className="page-header">
        <h1 className="page-header__title">Timesheets</h1>

      </div>

      <div className="task-tab-header">
        <div className="page-header__actions">
          <button className="btn-create" onClick={openCreate}>+ New</button>
        </div>
        <SearchBar search={search} setSearch={setSearch} placeholder="Search by project or task…" style={{ flex: 1, margin: 0 }} />

        <div className="view-toggle">
          <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            List
          </button>
          <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
            Kanban
          </button>
        </div>
      </div>

      <div className={`table-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading...</p>
          </div>
        )}
        {viewType === "list" ? (
          <table className="table">
            <thead>
              <tr>
                <th className="table__th">Date</th>
                <th className="table__th">Project</th>
                <th className="table__th">Task</th>
                <th className="table__th">Description</th>
                <th className="table__th">Hours</th>
                <th className="table__th" ></th>
              </tr>
            </thead>
            <tbody>
              {pagedData.length === 0 ? (
                <tr><td className="table__empty" colSpan={6}>No timesheets match your filter.</td></tr>
              ) : pagedData.map((ts) => (
                <tr key={ts.id} className="table__row" style={{ backgroundColor: getRowBgColor(ts, timesheets) }}>
                  <td className="table__td" ><span className="table__client">{ts.date}</span></td>
                  <td className="table__td"><span className="table__name" style={{ fontSize: '12px' }}>{ts.project}</span></td>
                  <td className="table__td"><span className="table__name" style={{ color: '#6b7fc4', fontWeight: '600' }}>{ts.task_name}</span></td>
                  <td className="table__td"><span className="table__client">{ts.description}</span></td>
                  <td className="table__td"><span className="table__value">{Number(ts.hours).toFixed(2)}h</span></td>
                  <td className="table__td" style={{ textAlign: 'right' }}>
                    <button className="btn-row-edit" onClick={() => {
                      const initialForm = {
                        project_id: ts.project_id_raw ? ts.project_id_raw[0] : "",
                        task: ts.description,
                        task_id: ts.task_id_raw ? ts.task_id_raw[0] : "",
                        task_name: ts.task_name,
                        date: ts.date,
                        hours: String(ts.hours)
                      };
                      lastSavedForm.current = initialForm;
                      setEditingTsId(ts.id);
                      setTsForm(initialForm);
                      setIsTsModalOpen(true);
                    }} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        ) : (
          <div className="task-grid">
            {pagedData.length === 0 ? (
              <div className="table__empty" style={{ padding: '40px' }}>No timesheets match your filter.</div>
            ) : pagedData.map((ts) => (
              <div key={ts.id} className="kanban-card" style={{ backgroundColor: getRowBgColor(ts, timesheets) }}>
                <div className="kanban-card__top" style={{ display: 'flex', justifyContent: 'flex-end' }} >
                  <div >
                    <button className="btn-row-edit" onClick={() => {
                      const initialForm = {
                        project_id: ts.project_id_raw ? ts.project_id_raw[0] : "",
                        task: ts.description,
                        task_id: ts.task_id_raw ? ts.task_id_raw[0] : "",
                        task_name: ts.task_name,
                        date: ts.date,
                        hours: String(ts.hours)
                      };
                      lastSavedForm.current = initialForm;
                      setEditingTsId(ts.id);
                      setTsForm(initialForm);
                      setIsTsModalOpen(true);
                    }} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: '#6b7fc4', fontWeight: '700', marginBottom: '4px' }}>{ts.task_name}</div>
                <div style={{ fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: '1.5', marginBottom: '12px' }}>{ts.description}</div>
                <div style={{ marginBottom: '16px', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>{ts.project}</div>
                <div className="kanban-card__bottom">
                  <div style={{ fontWeight: '700', color: '#1a2332' }}>{ts.date}</div>
                  <span className="kanban-card__id">{Number(ts.hours).toFixed(2)}h</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </div>
    </main>
  );
}
